/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { action, observable } from 'mobx';
import { LuisConstants } from 'src/core/utils/LuisConstants';

export type RowEditOperationType = 'add' | 'edit' | 'none';

export class QnaMakerRowMetricStore {
    public readonly id: number;

    @observable public timestampToStartEditing: number;

    @observable public operationType: RowEditOperationType;

    @observable public questionSectionClicksInEditMode: number;

    @observable public answerSectionClicksInEditMode: number;

    @observable public metadataSectionClicksInEditMode: number;

    @observable public timeTakenToEditQuestionsInMilliseconds: number;

    @observable public timeTakenToEditAnswersInMilliseconds: number;

    @observable public timeTakenToEditMetadataInMilliseconds: number;

    @observable public numberOfAddedQuestions: number;

    @observable public numberOfEditedQuestions: number;

    @observable public numberOfDeletedQuestions: number;

    @observable public numberOfAddedMetadata: number;

    @observable public numberOfDeletedMetadata: number;

    @observable public timeTakenToAddOrDismissMetadataOutsideEditMode: number;

    @observable public timeTakenToAddOrDismissAlternateQuestionOutsideEditMode: number;

    constructor(id: number) {
        this.id = id;

        this.operationType = 'none';
        this.numberOfAddedQuestions = 0;
        this.numberOfEditedQuestions = 0;
        this.numberOfDeletedQuestions = 0;
        this.numberOfAddedMetadata = 0;
        this.numberOfDeletedMetadata = 0;
        this.questionSectionClicksInEditMode = 0;
        this.answerSectionClicksInEditMode = 0;
        this.metadataSectionClicksInEditMode = 0;
        this.timeTakenToEditQuestionsInMilliseconds = 0;
        this.timeTakenToEditAnswersInMilliseconds = 0;
        this.timeTakenToEditMetadataInMilliseconds = 0;
        this.timeTakenToAddOrDismissMetadataOutsideEditMode = 0;
        this.timeTakenToAddOrDismissAlternateQuestionOutsideEditMode = 0;
    }

    @action
    public setOperationType(operationType: RowEditOperationType) {
        this.operationType = operationType;
    }

    @action
    public setTimestampToStartEditing(time: number) {
        this.timestampToStartEditing = time;
    }

    @action
    public increaseQuestionClicks() {
        this.questionSectionClicksInEditMode++;
    }

    @action increaseAnswersClicks() {
        this.answerSectionClicksInEditMode++;
    }

    @action increaseMetadataClicks() {
        this.metadataSectionClicksInEditMode++;
    }

    @action increaseAddedQuestions() {
        this.numberOfAddedQuestions++;
    }

    @action increaseEditedQuestions() {
        this.numberOfEditedQuestions++;
    }

    @action increaseDeletedQuestions() {
        this.numberOfDeletedQuestions++;
    }

    @action increaseAddedMetadata() {
        this.numberOfAddedMetadata++;
    }

    @action increaseDeletedMetadata() {
        this.numberOfDeletedMetadata++;
    }

    @action
    public setTimeTakenToEditQuestions(time: number) {
        this.timeTakenToEditQuestionsInMilliseconds += time;
    }

    @action
    public setTimeTakenToEditAnswers(time: number) {
        this.timeTakenToEditAnswersInMilliseconds += time;
    }

    @action
    public setTimeTakenToEditMetadata(time: number) {
        this.timeTakenToEditMetadataInMilliseconds += time;
    }

    @action
    public setTimeTakenToAddOrDismissMetadataOutsideEditMode(time: number) {
        this.timeTakenToAddOrDismissMetadataOutsideEditMode += time;
    }

    @action
    public setTimeTakenToAddOrDismissAlternateQuestionOutsideEditMode(time: number) {
        this.timeTakenToAddOrDismissAlternateQuestionOutsideEditMode += time;
    }

    @action
    public resetClicks() {
        this.operationType = 'none';
        this.numberOfAddedQuestions = 0;
        this.numberOfEditedQuestions = 0;
        this.numberOfDeletedQuestions = 0;
        this.numberOfAddedMetadata = 0;
        this.numberOfDeletedMetadata = 0;
        this.questionSectionClicksInEditMode = 0;
        this.answerSectionClicksInEditMode = 0;
        this.metadataSectionClicksInEditMode = 0;
    }

    @action
    public resetTimes() {
        this.timestampToStartEditing = 0;
        this.timeTakenToEditAnswersInMilliseconds = 0;
        this.timeTakenToEditQuestionsInMilliseconds = 0;
        this.timeTakenToEditMetadataInMilliseconds = 0;
        this.timeTakenToAddOrDismissMetadataOutsideEditMode = 0;
        this.timeTakenToAddOrDismissAlternateQuestionOutsideEditMode = 0;
    }

    public getRowInfoOnSubmit() {
        return {
            operationType: this.operationType,
            totalTimeToSubmit: Date.now() - this.timestampToStartEditing,
            totalNumberOfClicks:
                this.answerSectionClicksInEditMode +
                this.metadataSectionClicksInEditMode +
                this.questionSectionClicksInEditMode +
                LuisConstants.QnaMaker.Metrics.submitClickCounter +
                (this.operationType === 'add'
                    ? LuisConstants.QnaMaker.Metrics.addNewPairBaseClickCounter
                    : LuisConstants.QnaMaker.Metrics.editPairBaseClickCounter),
            totalTimeToEdit:
                this.timeTakenToEditAnswersInMilliseconds +
                this.timeTakenToEditMetadataInMilliseconds +
                this.timeTakenToEditQuestionsInMilliseconds +
                (this.operationType === 'add'
                    ? LuisConstants.QnaMaker.Metrics.addPairBaseTimeInMilliseconds
                    : LuisConstants.QnaMaker.Metrics.editPairBaseTimeInMilliseconds),
            numberOfAddedMetadata: this.numberOfAddedMetadata,
            numberOfDeletedMetadata: this.numberOfDeletedMetadata,
            numberOfAddedQuestions: this.numberOfAddedQuestions,
            numberOfEditedQuestions: this.numberOfEditedQuestions,
            numberOfDeletedQuestions: this.numberOfDeletedQuestions,
            numberOfClicksOfAnswers: this.answerSectionClicksInEditMode,
            numberOfClicksOfMetadata: this.metadataSectionClicksInEditMode,
            numberOfClicksOfQuestions: this.questionSectionClicksInEditMode,
            timeTakenToEditAnswers: this.timeTakenToEditAnswersInMilliseconds,
            timeTakenToEditMetadata: this.timeTakenToEditMetadataInMilliseconds,
            timeTakenToEditQuestions: this.timeTakenToEditQuestionsInMilliseconds
        };
    }
}
